import React, { useEffect, memo } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { IdentityProvider, useAuthState } from '@hiyve/react-identity';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import App from './App';
import { SettingsProvider } from './stores/settingsStore';

// Create a query client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Clear all cached data - called on auth state change
function clearAllCaches() {
  // Clear React Query cache
  queryClient.clear();

  // Clear old localStorage items that may contain user-specific data
  // Note: We preserve muzie_admin_* settings (page sizes, theme, etc.) as these are user preferences
  const keysToRemove = [
    'muzie-rtc-admin-searchQuery',
    'muzie-rtc-admin-filterZeros',
    'muzie-rtc-admin-onlyLive',
    'muzie-rtc-admin-recordings-searchQuery',
    'muzie-rtc-admin-onlyactive',
    'muzie-rtc-admin-rooms-rowsPerPage',
    'muzie-rtc-admin-recordings-rowsPerPage',
  ];
  keysToRemove.forEach(key => localStorage.removeItem(key));

  console.log('Cleared query cache and legacy localStorage');
}

/**
 * Handles auth state changes inside IdentityProvider context.
 * Clears caches on login/logout and auto-redirects to /login on session expiry.
 */
function AuthStateHandler() {
  const authState = useAuthState();
  const status = authState?.status ?? authState; // object with .status or plain string

  useEffect(() => {
    console.log('Auth state changed:', status, authState);

    if (status === 'authenticated' || status === 'unauthenticated') {
      clearAllCaches();
    }

    // Auto-redirect to login when session expires (but not during TFA flow)
    if (status === 'unauthenticated' && !authState?.tfaRequired) {
      if (!window.location.pathname.startsWith('/login') &&
          !window.location.pathname.startsWith('/signup') &&
          !window.location.pathname.startsWith('/forgot-password') &&
          !window.location.pathname.startsWith('/reset-password') &&
          !window.location.pathname.startsWith('/verify')) {
        window.location.href = '/login';
      }
    }
  }, [status]);

  return null;
}

const AppWrapper = memo(() => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <IdentityProvider>
          <AuthStateHandler />
          <SettingsProvider>
            <App />
          </SettingsProvider>
        </IdentityProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <AppWrapper />
);
